import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, DiscountUpdateEvent } from '@theme/events';

/**
 * Componente principal del cart drawer
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  open() {
    this.showDialog();

    // Notificar al manager que el drawer esta abierto
    recommendationsManager.setDrawerState(true);

    // Integracion con Shopify payment terms
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Cargar recomendaciones cuando se abre el drawer
    setTimeout(() => {
      recommendationsManager.loadRecommendations();
    }, 200);
  }

  close() {
    this.closeDialog();
    
    // Notificar al manager que el drawer esta cerrado
    recommendationsManager.setDrawerState(false);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* ------------------------------------------------------------------ */
/*  Utilidades de render y observer                                   */
/* ------------------------------------------------------------------ */

let cartDrawerObserver = null;

function setupCartDrawerObserver() {
  // Desconecta el anterior si existia
  if (cartDrawerObserver) {
    cartDrawerObserver.disconnect();
    cartDrawerObserver = null;
  }

  const cartDrawer = document.querySelector('.cart-drawer__content');
  if (!cartDrawer) return;

  cartDrawerObserver = new MutationObserver(
    debounce(async () => {
      if (giftSystem.activeOperations.size === 0 && !giftSystem.isProcessing) {
        const cart = await fetchCart();
        if (!cart || cart.item_count === 0) {
          await handleEmptyCart();
        } else {
          processCartUpdate();
        }
      }
    }, 500)
  );

  cartDrawerObserver.observe(cartDrawer, {
    childList: true,
    subtree: true,
  });
}

async function renderDrawerContent() {
  try {
    const response = await fetch('/?sections=cart-drawer-wrapper');
    const data = await response.json();

    const html = data['cart-drawer-wrapper'];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Detectar estados actual y nuevo
    const currentCartItemsComponent = document.querySelector('cart-items-component');
    const newCartItemsComponent = doc.querySelector('cart-items-component');
    
    if (newCartItemsComponent && currentCartItemsComponent) {
      const currentIsEmpty = document.querySelector('.cart-drawer--empty');
      const newIsEmpty = doc.querySelector('.cart-drawer--empty');
      
      // CASO CRITICO: Transicion de vacio a productos
      if (currentIsEmpty && !newIsEmpty) {
        console.log('Transicion critica: vacio -> productos');
        
        // 1. Reemplazar TODO el contenido del cart-items-component
        currentCartItemsComponent.innerHTML = newCartItemsComponent.innerHTML;
        
        // 2. Actualizar la clase del dialog
        const dialog = document.querySelector('.cart-drawer__dialog');
        if (dialog) {
          dialog.classList.remove('cart-drawer--empty');
        }
        
        // 3. IMPORTANTE: Reemplazar tambien el header si es necesario
        const currentHeader = document.querySelector('.cart-drawer__header');
        const newHeader = doc.querySelector('.cart-drawer__header');
        
        if (currentHeader && newHeader) {
          currentHeader.replaceWith(newHeader);
        }
      }
      // Caso: Estado vacio (reemplazar todo)
      else if (newIsEmpty) {
        currentCartItemsComponent.innerHTML = newCartItemsComponent.innerHTML;
        
        // Agregar clase empty al dialog si no la tiene
        const dialog = document.querySelector('.cart-drawer__dialog');
        if (dialog && !dialog.classList.contains('cart-drawer--empty')) {
          dialog.classList.add('cart-drawer--empty');
        }
      }
      // Caso: Estado normal productos a productos (reemplazar solo partes)
      else if (!newIsEmpty && !currentIsEmpty) {
        const newItems = doc.querySelector('.cart-drawer__items');
        const newSummary = doc.querySelector('.cart-drawer__summary');
        const newHeader = doc.querySelector('.cart-drawer__header');
        
        const curItems = document.querySelector('.cart-drawer__items');
        const curSummary = document.querySelector('.cart-drawer__summary');
        const curHeader = document.querySelector('.cart-drawer__header');

        if (newItems && curItems) {
          curItems.replaceWith(newItems);
        }
        if (newSummary && curSummary) {
          curSummary.replaceWith(newSummary);
        }
        if (newHeader && curHeader) {
          curHeader.replaceWith(newHeader);
        }
      }
    }

    // Reatar observador
    setupCartDrawerObserver?.();

    // Notificar cambio a recomendaciones
    recommendationsManager.handleCartChange();

    // Actualizar UI del gift system
    const fresh = await fetchCart();
    if (fresh) giftSystem.updateUI(fresh);

    return true;
  } catch (error) {
    console.error('Error updating cart drawer:', error);
    return false;
  }
}

// Funcion para manejar carrito vacio
async function handleEmptyCart() {
  const cart = await fetchCart();
  
  if (!cart || cart.item_count === 0) {
    giftSystem.reset();
    recommendationsManager.clearCache();
    
    await renderDrawerContent();
    
    const cartDrawer = document.querySelector('.cart-drawer__content');
    if (cartDrawer) {
      const emptyState = cartDrawer.querySelector('.cart-drawer__heading--empty, [data-empty-cart]');
      if (!emptyState) {
        console.log('Forzando recarga para estado vacio...');
      }
    }
    
    return true;
  }
  
  return false;
}

// NUEVA: Funcion para manejar primer producto agregado


/**
 * Gestor de recomendaciones de productos OPTIMIZADO
 */
class RecommendationsManager {
  constructor() {
    this.cache = new Map();
    this.isLoading = false;
    this.maxRecommendations = 3;
    this.lastCartSignature = null;
    this.loadTimeout = null;
    this.isDrawerOpen = false;
    this.hasLoadedForCurrentCart = false;
  }

  async loadRecommendations(forceReload = false) {
    // Si ya esta cargando, no hacer nada
    if (this.isLoading && !forceReload) return;
    
    const cart = await fetchCart();
    if (!cart || cart.item_count === 0) {
      this.hideRecommendations();
      return;
    }

    const container = document.querySelector('#cart-recommendations');
    if (!container) return;

    // Crear signature del carrito para detectar cambios reales
    const cartSignature = this.createCartSignature(cart);
    
    // Si el carrito no cambio y ya cargamos para este carrito, no recargar
    if (!forceReload && 
        cartSignature === this.lastCartSignature && 
        this.hasLoadedForCurrentCart) {
      return;
    }

    // Cancelar timeout anterior si existe
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    // Debounce la carga para evitar multiples llamadas seguidas
    this.loadTimeout = setTimeout(async () => {
      await this.performLoad(cart, container, cartSignature);
    }, 150);
  }

  async performLoad(cart, container, cartSignature) {
  // Evitar llamadas simultáneas
  if (this.isLoading) return;

  this.isLoading = true;

  // Detectar si ya hay contenido previo
  const existingGrid = container.querySelector('.cart-recommendations__grid');
  const hasExistingContent = existingGrid && existingGrid.children.length > 0;

  // Mostrar loader solo si no hay contenido previo
  if (!hasExistingContent) {
    this.showLoading(container);
  }

   try {
      const recommendations = await this.getRecommendations(cart);
  
      // 🔹 Si no hay recomendaciones, simplemente ocultamos la sección y salimos.
      if (!recommendations || recommendations.length === 0) {
        this.hideRecommendations(container);
        return; 
      }
      this.renderRecommendations(recommendations, container);
      this.lastCartSignature = cartSignature; // Guardamos la firma que inició la carga.
      this.hasLoadedForCurrentCart = true;
  
    } catch (error) {
      console.error('Error loading recommendations:', error);
      this.hideRecommendations(container); // Ocultar en caso de error
    } finally {
      // 🔹 Resetear flags de carga
      this.isLoading = false;
      this.loadTimeout = null;
    }
  }

  // Crear signature unica del carrito basada en productos
  createCartSignature(cart) {
    if (!cart || !cart.items) return 'empty';
    
    return cart.items
      .map(item => `${item.product_id}:${item.variant_id}`)
      .sort()
      .join('|');
  }

  async getRecommendations(cart) {
  const productIdsInCart = [...new Set(cart.items.map(item => item.product_id))];
  const primaryProductId = productIdsInCart[0];

  // Cache más inteligente - considera los primeros 3 productos
  const extendedCacheKey = `cart-${primaryProductId}-${productIdsInCart.slice(0, 3).join(',')}`;
  if (this.cache.has(extendedCacheKey)) {
    return this.cache.get(extendedCacheKey);
  }

  try {
    const response = await fetch(`/recommendations/products.json?product_id=${primaryProductId}&limit=${this.maxRecommendations * 3}`);
    if (!response.ok) throw new Error('Failed to fetch recommendations');

    const data = await response.json();

    // Filtrar productos que ya estén en el carrito o no tengan stock
    const filteredProducts = data.products.filter(product => {
      const firstVariant = product.variants?.[0];
      return firstVariant && firstVariant.available && !productIdsInCart.includes(product.id);
    });

    // Si no hay productos válidos, devolver arreglo vacío para ocultar sección
    const recommendationsToReturn = filteredProducts.slice(0, this.maxRecommendations);

    // Cache con TTL implícito
    this.cache.set(extendedCacheKey, recommendationsToReturn);
    setTimeout(() => {
      this.cache.delete(extendedCacheKey);
    }, 5 * 60 * 1000); // 5 minutos

    return recommendationsToReturn; // <-- puede ser vacío

  } catch (error) {
    console.error('Error fetching recommendations:', error);
    return []; // devolver vacío si hay error
  }
}

  showLoading(container) {
    const loading = container.querySelector('.cart-recommendations__loading');
    const grid = container.querySelector('.cart-recommendations__grid');

    if (loading) loading.style.display = 'block';
    if (grid) grid.style.display = 'none';
  }

  hideRecommendations(container = null) {
    const target = container || document.querySelector('#cart-recommendations');
    if (target) {
      target.style.display = 'none';
    }
    this.hasLoadedForCurrentCart = false;
  }

  renderRecommendations(products, container) {
    if (!products || products.length === 0) {
      this.hideRecommendations(container);
      return;
    }

    const loading = container.querySelector('.cart-recommendations__loading');
    const grid = container.querySelector('.cart-recommendations__grid');

    if (loading) loading.style.display = 'none';
    if (!grid) return;

    // Usar DocumentFragment para mejor rendimiento
    const fragment = document.createDocumentFragment();
    
    products.forEach(product => {
      const productCard = this.createProductCard(product);
      if (productCard) {
        fragment.appendChild(productCard);
      }
    });

    // Solo limpiar y agregar si hay cambios reales
    const currentProducts = Array.from(grid.querySelectorAll('.product-card-small')).map(
      card => card.dataset.productId
    ).join(',');
    
    const newProducts = products.map(p => p.id.toString()).join(',');
    
    if (currentProducts !== newProducts) {
      grid.innerHTML = '';
      grid.appendChild(fragment);
    }

    grid.style.display = 'grid';
    container.style.display = 'block';
  }

  createProductCard(product) {
    const wrapper = document.createElement('div');
    const variant = product.variants?.[0];
    if (!variant || !variant.available) return null;

    const price = this.formatPrice(variant.price);

    wrapper.innerHTML = `
      <div class="product-card-small product-card-small--grid" data-product-id="${product.id}">
        <div class="product-card-small__image-wrapper">
          <div class="product-card-small__image product-card-small__image--grid">
            <img src="${product.featured_image}" alt="${product.title}" loading="lazy">
          </div>
          
          <button 
            class="product-card-small__quick-add product-card-small__quick-add--overlay" 
            type="button" 
            data-variant-id="${variant.id}"
          >+</button>
        </div>
        
        <div class="product-card-small__info product-card-small__info--grid">
          <h4 class="product-card-small__title">${product.title}</h4>
          <div class="product-card-small__price">
            <span class="product-card-small__price-current">${price}</span>
          </div>
        </div>
      </div>
    `;

    // Event listener para el boton
    const addButton = wrapper.querySelector('.product-card-small__quick-add--overlay');
    if (addButton) {
      addButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart(variant.id, product.title, addButton);
      });
    }

    return wrapper.firstElementChild;
  }

   async addToCart(variantId, productTitle, button) {
    // Prevenimos múltiples clics seguidos
    button.disabled = true;
    // Añadimos una clase para el efecto visual de 'parpadeo'
    button.classList.add('is-clicked');

    // Restauramos el botón después de un breve instante (600ms)
    setTimeout(() => {
      button.classList.remove('is-clicked');
      button.disabled = false;
    }, 600);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      });

      if (!response.ok) throw new Error('Error adding product to cart');

      this.hasLoadedForCurrentCart = false;
      
      // Disparamos el evento para que el carrito se actualice de forma centralizada
      document.dispatchEvent(new CustomEvent('cart:refresh'));

    } catch (error) {
      console.error('Error adding recommendation:', error);
      // Si hay un error, el botón ya se habrá restaurado por el setTimeout,
      // así que la experiencia es consistente para el usuario.
    }
  }

  formatPrice(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  clearCache() {
    this.cache.clear();
    this.lastCartSignature = null;
    this.hasLoadedForCurrentCart = false;
  }

  setDrawerState(isOpen) {
    const wasOpen = this.isDrawerOpen;
    this.isDrawerOpen = isOpen;
    
    // Solo cargar si se acaba de abrir el drawer y no hemos cargado para este carrito
    if (isOpen && !wasOpen && !this.hasLoadedForCurrentCart) {
      setTimeout(() => {
        this.loadRecommendations();
      }, 200);
    }
  }

  handleCartChange() {
    // Resetear el flag para permitir nueva carga
    this.hasLoadedForCurrentCart = false;
    
    // Solo cargar si el drawer esta abierto
    if (this.isDrawerOpen) {
      this.loadRecommendations();
    }
  }
}

/* ------------------------------------------------------------------ */
/*  GiftSystemController                                               */
/* ------------------------------------------------------------------ */

class GiftSystemController {
  constructor() {
    this.isProcessing = false;
    this.activeOperations = new Set();
    this.lastProcessedCart = null;
    this.config = null;
    this.initialized = false;
    this.checkpoints = null;
    this.lastRatio = 0;
    this.justRehydrated = false;
  }

  initialize() {
    if (this.initialized) return true;

    const progressContainer = document.querySelector('.cart-progress');
    if (!progressContainer) return false;

    // Lectura robusta de data-*
    const getNum = (val, fallback) =>
      parseInt(val ?? '', 10) || parseInt(fallback, 10);

    const ds = progressContainer.dataset;

    this.config = {
      firstThreshold: getNum(
        ds.freeShippingMinimum ?? progressContainer.getAttribute('data-free-shipping-minimum'),
        '5000'
      ),
      secondThreshold: getNum(
        ds.gift1Threshold ?? progressContainer.getAttribute('data-gift-1-threshold'),
        '15000'
      ),
      thirdThreshold: getNum(
        ds.gift2Threshold ?? progressContainer.getAttribute('data-gift-2-threshold'),
        '20000'
      ),
      gift1VariantId: getNum(
        ds.gift1VariantId ?? progressContainer.getAttribute('data-gift-1-variant-id'),
        '0'
      ),
      gift2VariantId: getNum(
        ds.gift2VariantId ?? progressContainer.getAttribute('data-gift-2-variant-id'),
        '0'
      ),

      gift1Title: ds.gift1Title || progressContainer.getAttribute('data-gift-1-title') || '',
      gift2Title: ds.gift2Title || progressContainer.getAttribute('data-gift-2-title') || '',
      locale: ds.locale || 'es-ES',
      currency: ds.currency || 'EUR',
      msgAll: ds.msgAll || 'Has desbloqueado todos los regalos!',
      msgNext: ds.msgNext || 'Gasta __AMOUNT__ mas para obtener __GIFT__',
      msgShip: ds.msgShip || 'Gasta __AMOUNT__ mas para envio gratis',
      shippingTitle: ds.shippingTitle || progressContainer.getAttribute('data-shipping-title') || 'Free shipping',
      msgNextGeneric: ds.msgNextGeneric || progressContainer.getAttribute('data-msg-next-generic') || 'Spend __AMOUNT__ more to get __MILESTONE__',
    };

    // Crear checkpoints dentro de la barra
    this.upsertCheckpoints();

    this.initialized = true;
    return true;
  }

  // Garantiza que existan checkpoints y que sus nodos sigan en el DOM
  ensureCheckpoints() {
    const bar = document.querySelector('.cart-progress .free-shipping--bar');
    if (!bar) return;

    const host = bar.querySelector('.free-shipping--checkpoints');
    const nodesAlive =
      this.checkpoints &&
      Object.values(this.checkpoints).every((el) => el && el.isConnected);

    if (!host || !nodesAlive) {
      this.upsertCheckpoints();
    }
  }

  // Crea/actualiza 3 checkpoints sin borrar los existentes
  upsertCheckpoints() {
    const bar = document.querySelector('.cart-progress .free-shipping--bar');
    if (!bar) return;

    let host = bar.querySelector('.free-shipping--checkpoints');
    if (!host) {
      host = document.createElement('div');
      host.className = 'free-shipping--checkpoints';
      bar.appendChild(host);
    }

    const { firstThreshold, secondThreshold, thirdThreshold } = this.config;
    const max = Math.max(firstThreshold || 0, secondThreshold || 0, thirdThreshold || 0, 1);

    // SVGs
    const SVG_FIRST = `
<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#FFFFFF"><path d="M240-192q-50 0-85-35t-35-85H48v-408q0-29.7 21.15-50.85Q90.3-792 120-792h552v144h120l120 168v168h-72q0 50-35 85t-85 35q-50 0-85-35t-35-85H360q0 50-35 85t-85 35Zm0-72q20.4 0 34.2-13.8Q288-291.6 288-312q0-20.4-13.8-34.2Q260.4-360 240-360q-20.4 0-34.2 13.8Q192-332.4 192-312q0 20.4 13.8 34.2Q219.6-264 240-264ZM120-384h24q17-23 42-35.5t54-12.5q29 0 54 12.5t41.53 35.5H600v-336H120v336Zm600 120q20.4 0 34.2-13.8Q768-291.6 768-312q0-20.4-13.8-34.2Q740.4-360 720-360q-20.4 0-34.2 13.8Q672-332.4 672-312q0 20.4 13.8 34.2Q699.6-264 720-264Zm-48-192 168-1-85-119h-83v120Zm-310-93Z"/></svg>`;

    const SVG_OTHER = `
<svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#FFFFFF"><path d="M168-96v-432H96v-216h184q-5-11-7.5-22.94Q270-778.87 270-792q0-50 35-85t85-35q26.29 0 49.14 11Q462-890 480-871q17-19 40.5-30t49.5-11q50 0 85 35t35 85q0 12-3.5 24t-6.5 24h184v216h-72v432H168Zm402-744q-20.4 0-34.2 13.8Q522-812.4 522-792q0 20.4 13.8 34.2Q549.6-744 570-744q20.4 0 34.2-13.8Q618-771.6 618-792q0-20.4-13.8-34.2Q590.4-840 570-840Zm-228 48q0 20.4 13.8 34.2Q369.6-744 390-744q20.4 0 34.2-13.8Q438-771.6 438-792q0-20.4-13.8-34.2Q410.4-840 390-840q-20.4 0-34.2 13.8Q342-812.4 342-792ZM168-672v72h276v-72H168Zm276 504v-360H240v360h204Zm72 0h204v-360H516v360Zm276-432v-72H516v72h276Z"/></svg>`;

    const ensure = (key, threshold) => {
      const sel = `.free-shipping--checkpoint[data-key="${key}"]`;
      let el = host.querySelector(sel);
      const left = Math.min(100, Math.max(0, (threshold / max) * 100));
      if (!el) {
        el = document.createElement('div');
        el.className = 'free-shipping--checkpoint';
        el.dataset.key = key;
        el.innerHTML = key === 'first' ? SVG_FIRST : SVG_OTHER;
        host.appendChild(el);
      }
      el.style.left = `${left}%`;
      return el;
    };

    this.checkpoints = {
      first: ensure('first', firstThreshold),
      second: ensure('second', secondThreshold),
      third: ensure('third', thirdThreshold),
    };
  }

  shouldProcess(cart) {
    if (this.isProcessing) return false;
    if (!this.initialized) return false;
    
    // Si el carrito esta vacio, siempre procesar para limpiar
    if (!cart || cart.item_count === 0) {
      return true;
    }

    const cartSignature = `${cart.total_price}-${cart.item_count}-${cart.items.length}`;
    const lastSignature = this.lastProcessedCart
      ? `${this.lastProcessedCart.total_price}-${this.lastProcessedCart.item_count}-${this.lastProcessedCart.items.length}`
      : null;

    if (cartSignature === lastSignature) return false;

    this.lastProcessedCart = JSON.parse(JSON.stringify(cart));
    return true;
  }

  async processCart(cart) {
    // Si la barra cambio tras un re-render, asegurarse de reinyectar
    this.ensureCheckpoints();

    if (!this.shouldProcess(cart)) return;

    this.isProcessing = true;
    try {
      // Si el carrito esta vacio, solo limpiar UI
      if (!cart || cart.item_count === 0) {
        this.clearProgressUI();
        this.lastProcessedCart = null;
        return;
      }

      await this.handleGifts(cart);
      this.updateUI(cart);
    } finally {
      this.isProcessing = false;
    }
  }

  async handleGifts(cart) {
    const total = cart.total_price;
    const gift1InCart = this.isProductInCart(cart, this.config.gift1VariantId);
    const gift2InCart = this.isProductInCart(cart, this.config.gift2VariantId);

    const shouldHaveGift1 = total >= this.config.secondThreshold;
    if (shouldHaveGift1 && !gift1InCart && !this.activeOperations.has('gift1')) {
      await this.addGift(this.config.gift1VariantId, 'gift1');
    } else if (!shouldHaveGift1 && gift1InCart && !this.activeOperations.has('gift1')) {
      await this.removeGift(cart, this.config.gift1VariantId, 'gift1');
    }

    const shouldHaveGift2 = total >= this.config.thirdThreshold;
    if (shouldHaveGift2 && !gift2InCart && !this.activeOperations.has('gift2')) {
      await this.addGift(this.config.gift2VariantId, 'gift2');
    } else if (!shouldHaveGift2 && gift2InCart && !this.activeOperations.has('gift2')) {
      await this.removeGift(cart, this.config.gift2VariantId, 'gift2');
    }
  }

  async addGift(variantId, giftKey) {
    if (this.activeOperations.has(giftKey)) return;

    this.activeOperations.add(giftKey);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _gift: giftKey },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Error adding gift to cart');
      }

      debouncedUnifiedCartUpdate();

    } catch (error) {
      console.error(`Failed to add ${giftKey}:`, error);
    } finally {
      this.activeOperations.delete(giftKey);
    }
  }

  async removeGift(cart, variantId, giftKey) {
    if (this.activeOperations.has(giftKey)) return;

    const lineItem = cart.items.find((item) => item.variant_id == variantId);
    if (!lineItem) return;

    this.activeOperations.add(giftKey);

    try {
      const lineIndex = cart.items.findIndex((item) => item.variant_id == variantId) + 1;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error(`Remove failed: ${response.status}`);
      }
      
      debouncedUnifiedCartUpdate();
      
      
    } catch (error) {
      console.error(`Error removing ${giftKey}:`, error);
    } finally {
      this.activeOperations.delete(giftKey);
    }
  }

  isProductInCart(cart, variantId) {
    return cart.items.some((item) => item.variant_id == variantId);
  }

  clearProgressUI() {
    const progressContainer = document.querySelector('.cart-progress');
    if (!progressContainer) return;

    // Resetear progreso a 0
    progressContainer.style.setProperty('--progress', 0);
    
    // Desactivar todos los checkpoints
    if (this.checkpoints) {
      Object.values(this.checkpoints).forEach(checkpoint => {
        if (checkpoint) {
          checkpoint.classList.remove('is-active');
        }
      });
    }

    // Limpiar texto de progreso
    const shippingText = progressContainer.querySelector('.free-shipping--text');
    if (shippingText) {
      shippingText.textContent = '';
    }

    // Desactivar ticks y milestones
    const tickIcons = progressContainer.querySelectorAll('.cart-ticks svg path');
    const milestoneTicks = document.querySelectorAll('.milestone__tick svg path');
    const milestones = progressContainer.querySelectorAll('.free-shipping--milestones .milestone');

    tickIcons.forEach(path => path.setAttribute('fill', '#ccc'));
    milestoneTicks.forEach(path => path.setAttribute('fill', '#ccc'));
    milestones.forEach(el => el.classList.remove('active'));
  }

  updateUI(cart) {
    // Asegura checkpoints por si el DOM cambio
    this.ensureCheckpoints();

    const progressContainer = document.querySelector('.cart-progress');
    if (!progressContainer) return;

    const shippingText = progressContainer.querySelector('.free-shipping--text');
    const tickIcons = progressContainer.querySelectorAll('.cart-ticks svg path');
    const milestoneTicks = document.querySelectorAll('.milestone__tick svg path');
    const milestones = progressContainer.querySelectorAll('.free-shipping--milestones .milestone');

    const total = Number(cart.total_price) || 0;
    const { firstThreshold, secondThreshold, thirdThreshold } = this.config;

    // 1) Activacion de ticks/milestones
    const reached = [
      total >= (firstThreshold || 0),
      total >= (secondThreshold || 0),
      total >= (thirdThreshold || 0),
    ];

    [tickIcons, milestoneTicks].forEach(nodes => {
      nodes.forEach((path, i) => {
        path.setAttribute('fill', reached[i] ? '#15a52a' : '#ccc');
      });
    });
    milestones.forEach((el, i) => el.classList.toggle('active', !!reached[i]));

    // 2) Porcentaje continuo (0..1) y guardado en el CONTENEDOR para evitar "flash"
    const maxThreshold = Math.max(firstThreshold || 0, secondThreshold || 0, thirdThreshold || 0, 1);
    let ratio = total / maxThreshold;
    if (!Number.isFinite(ratio)) ratio = 0;
    ratio = Math.max(0, Math.min(1, ratio));

    // Persistir el progreso en el contenedor
    progressContainer.style.setProperty('--progress', ratio);
    this.lastRatio = ratio;

    // 3) Activacion de checkpoints internos
    if (this.checkpoints) {
      const { first, second, third } = this.checkpoints;
      if (first) first.classList.toggle('is-active', reached[0]);
      if (second) second.classList.toggle('is-active', reached[1]);
      if (third) third.classList.toggle('is-active', reached[2]);
    }

    // 4) Texto dinamico
    if (shippingText) {
      const {
        firstThreshold,
        secondThreshold,
        thirdThreshold,
        gift1Title,
        gift2Title,
        shippingTitle,
        locale,
        currency,
        msgAll,
        msgNextGeneric
      } = this.config;

      const total = Number(cart.total_price) || 0;
      const fmtMoney = (cents) =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Math.max(0, cents) / 100);

      // Construir milestones validos (umbral > 0)
      const milestones = [
        { key: 'shipping', threshold: firstThreshold || 0, label: shippingTitle || 'Free shipping' },
        { key: 'gift1', threshold: secondThreshold || 0, label: gift1Title || '' },
        { key: 'gift2', threshold: thirdThreshold || 0, label: gift2Title || '' },
      ].filter(m => m.threshold > 0);

      // Ordenar por umbral ascendente
      milestones.sort((a, b) => a.threshold - b.threshold);

      // Buscar el siguiente milestone pendiente
      const next = milestones.find(m => total < m.threshold);

      if (!next) {
        shippingText.textContent = msgAll;
      } else {
        const remaining = Math.max(0, next.threshold - total);
        const amountStr = fmtMoney(remaining);
        const labelStr = next.label || (next.key === 'shipping' ? 'Free shipping' : 'the next gift');

        let baseMessage = msgNextGeneric
          .replace('__AMOUNT__', amountStr)
          .replace('__MILESTONE__', `<strong>${labelStr}</strong>`);

        // Si ya se desbloqueo el free shipping y aun quedan milestones
        if (total >= firstThreshold && next.key !== 'gift2') {
          const msgFree = this.config.msgFree || progressContainer.getAttribute('data-msg-free') || '';
          if (msgFree) {
            baseMessage = `${msgFree} ${baseMessage}`;
          }
        }

        shippingText.innerHTML = baseMessage;
      }
    }
  }

  reset() {
    this.isProcessing = false;
    this.activeOperations.clear();
    this.lastProcessedCart = null;
    this.lastRatio = 0;
    
    // Limpiar UI cuando se resetea
    this.clearProgressUI();
  }

  async fetchFreshCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching fresh cart:', error);
      return null;
    }
  }

  refreshCartUI() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartIcon = document.querySelector('.cart-icon');
    const cartCount = document.querySelector('.cart-count');

    if (cartDrawer) {
      cartDrawer.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    setTimeout(async () => {
      const freshCart = await this.fetchFreshCart();
      if (freshCart && cartCount) {
        cartCount.textContent = freshCart.item_count;
      }
    }, 100);
  }
}

// Instancias globales
const giftSystem = new GiftSystemController();
const recommendationsManager = new RecommendationsManager();

/* ------------------------------------------------------------------ */
/*  Fetch + debounce + wiring                                         */
/* ------------------------------------------------------------------ */

async function fetchCart() {
  try {
    const response = await fetch('/cart.js');
    return await response.json();
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const processCartUpdate = debounce(async () => {
  if (!giftSystem.initialized && !giftSystem.initialize()) {
    return;
  }

  // Verificar estado vacio primero
  const isEmpty = await handleEmptyCart();
  if (isEmpty) {
    return;
  }

  const cart = await fetchCart();
  if (!cart) return;

  await giftSystem.processCart(cart);

  if (document.querySelector('.cart-drawer__dialog[open]')) {
    recommendationsManager.handleCartChange();
  }
}, 300);
async function unifiedCartUpdate() {
  console.log('Unified Cart Update Triggered...');

  // 1. Obtener el estado más reciente del carrito.
  const cart = await fetchCart();

  // 2. Manejar el caso de carrito vacío primero.
  if (!cart || cart.item_count === 0) {
    await handleEmptyCart();
    return;
  }

  // 3. Renderizar el contenido principal. Esto asegura que todos los elementos
  // (productos, barra de progreso, etc.) existan en el DOM.
  await renderDrawerContent();

  // 4. Inicializar el sistema de regalos DESPUÉS de renderizar.
  if (!giftSystem.initialized) {
    giftSystem.initialize();
  }
  
  // 5. Procesar la lógica del carrito (regalos) y actualizar la UI de la barra.
  await giftSystem.processCart(cart);

  // 6. Si el drawer está abierto, actualizar las recomendaciones.
  if (document.querySelector('.cart-drawer__dialog[open]')) {
    recommendationsManager.handleCartChange();
  }
}

// Creamos una versión "debounced" de nuestra función. Esto previene
// que se ejecute múltiples veces seguidas, solucionando la "carrera".
const debouncedUnifiedCartUpdate = debounce(unifiedCartUpdate, 300);
window.debouncedUnifiedCartUpdate = debouncedUnifiedCartUpdate;

// Event listeners

// 1) Primera carga: inicializa y pinta según el estado real.
document.addEventListener('DOMContentLoaded', async () => {
  setupCartDrawerObserver();
  // Llamamos a la función unificada para la carga inicial.
  unifiedCartUpdate();
});

// 2) Todos los eventos que modifican el carrito ahora llaman a la misma función debounced.
document.addEventListener(CartAddEvent.eventName, debouncedUnifiedCartUpdate);
document.addEventListener('cart:refresh', debouncedUnifiedCartUpdate);
document.addEventListener('cart:first-item-added', debouncedUnifiedCartUpdate);
document.addEventListener(DiscountUpdateEvent.eventName, debouncedUnifiedCartUpdate);

// 3) Evento específico para vaciar el carrito (puede ser inmediato).
document.addEventListener('cart:emptied', async () => {
  await handleEmptyCart();
});

// 4) Listeners de UI que no modifican el carrito.
document.addEventListener('click', (e) => {
  if (e.target.closest('[on\\:click="/close"]') || 
      e.target.closest('.cart-drawer__overlay')) {
    recommendationsManager.setDrawerState(false);
  }
});

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    recommendationsManager.clearCache();
  }
});

// 5) Evento especifico para cuando el carrito se vacia completamente
document.addEventListener('cart:emptied', async () => {
  console.log('Carrito completamente vacio, limpiando estado...');
  await handleEmptyCart();
});

// 5.1) NUEVO: Evento para cuando se agrega el primer producto a un carrito vacio
document.addEventListener('cart:first-item-added', async (event) => {
  console.log('Primer producto agregado al carrito vacio...');
});

// 6) Update con payload
document.addEventListener('cart:updated', async (event) => {
  const cart = event.detail?.cart;
  
  if (!cart || cart.item_count === 0) {
    await handleEmptyCart();
    return;
  }

  if (!giftSystem.isProcessing) {
    giftSystem.updateUI(cart);

    if (document.querySelector('.cart-drawer__dialog[open]')) {
      recommendationsManager.handleCartChange();
    }
  }
});

// 7) Listener para detectar cuando se cierra el drawer
document.addEventListener('click', (e) => {
  if (e.target.closest('[on\\:click="/close"]') || 
      e.target.closest('.cart-drawer__overlay')) {
    recommendationsManager.setDrawerState(false);
  }
});

// 8) Listener para cambios de visibilidad de la pagina
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Limpiar timeouts cuando la pagina no es visible
    recommendationsManager.clearCache();
  }
});

